import { Group, Stack, Text, type TextInputProps } from '@mantine-8/core';
import isNil from 'lodash/isNil';
import { type FC } from 'react';
import z from 'zod';
import FilterNumberInput from './FilterNumberInput';
import styles from './FilterNumberRangeInput.module.css';

interface Props extends Omit<TextInputProps, 'type' | 'value' | 'onChange'> {
    value?: unknown[];
    onChange: (value: unknown[]) => void;
}

const numberRangeSchema = z
    .tuple([z.number().nullable().optional(), z.number().nullable().optional()])
    .superRefine(([min, max], ctx) => {
        if (isNil(min) || isNil(max)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Both values are required',
                fatal: true,
            });
            return z.NEVER;
        }
        if (min > max) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Minimum should be less than the maximum',
            });
        }
    });

const FilterNumberRangeInput: FC<Props> = ({
    value,
    disabled,
    placeholder,
    onChange,
    autoFocus,
    ...rest
}) => {
    const validationResult = numberRangeSchema.safeParse(value);
    const errorMessage = validationResult.error
        ? validationResult.error.issues[0].message // only show one issue at a time
        : undefined;

    return (
        <Stack gap={2} w="100%">
            <Group wrap="nowrap" align="start" gap="xs">
                <FilterNumberInput
                    error={!!errorMessage}
                    disabled={disabled}
                    autoFocus={true}
                    placeholder="Min value"
                    {...rest}
                    value={value?.[0]}
                    onChange={(newValue) => {
                        onChange([newValue, value?.[1]]);
                    }}
                />

                <Text c="dimmed" mt={7} className={styles.noWrap} fz="xs">
                    –
                </Text>

                <FilterNumberInput
                    error={!!errorMessage}
                    disabled={disabled}
                    placeholder="Max value"
                    {...rest}
                    value={value?.[1]}
                    onChange={(newValue) => {
                        onChange([value?.[0], newValue]);
                    }}
                />
            </Group>
            {errorMessage && (
                <Text c="red" fz="xs">
                    {errorMessage}
                </Text>
            )}
        </Stack>
    );
};

export default FilterNumberRangeInput;
